import type React from 'react';
import { useTranslation } from 'react-i18next';
import type { SingleValue } from 'react-select';

import { getPaginatedCompaniesNames } from '@/services/ws-permissions/src/main/resources/rest-api';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { useModalState } from '@/js/libs/hooks/useModalState';
import { useSubscribe } from '@/js/libs/observable';
import { AsyncSelect } from '@/js/libs/ui/src/V2';
import { useAuth, useIdentityUser, useSelectCompany, type CompanyIdentifiersFragment } from '@/js/libs/use-auth';
import { useLazyRequest } from '@/js/libs/use-request';

import type { Option } from 'src/types.ts';
import { VerifyPinModal } from './containers/VerifyPinModal/index.tsx';

const CompaniesSwitch: React.FC = () => {
  const getPaginatedCompaniesNamesRequest = useLazyRequest({
    apiMethod: getPaginatedCompaniesNames,
  });

  const [t] = useTranslation(['common']);
  const { selectedCompany } = useAuth();
  const selectCompany = useSelectCompany();
  const currentUser = useIdentityUser();

  const pinModalState = useModalState<CompanyIdentifiersFragment>();

  const loadOptions = (prefix: string) =>
    getPaginatedCompaniesNamesRequest.load({ filter: prefix }).then(({ data }) =>
      data.items.map(
        (item): Option<CompanyIdentifiersFragment> => ({
          label: item.name,
          value: {
            id: item.id,
            companyUUID: item.companyUUID,
            name: item.name,
            temporaryAccessRequired: item.temporaryAccessRequired,
          },
        }),
      ),
    );
  const pending = useSubscribe(getPaginatedCompaniesNamesRequest.pending$);

  const handleChange = useCallbackRef((option: SingleValue<Option<CompanyIdentifiersFragment>>) => {
    if (!selectedCompany && !option) {
      return;
    }
    const isAdministrating = option ? (currentUser?.administratingCompanyIds?.includes(option.value.id.toString()) ?? false) : false;
    if (option?.value.temporaryAccessRequired && !isAdministrating) {
      pinModalState.open(option.value);
      return;
    }
    selectCompany(option?.value ?? null);
  });

  const handlePinConfirmed = useCallbackRef((sessionExpiresAt: number) => {
    if (!pinModalState.isMounted) {
      return;
    }
    selectCompany(pinModalState.payload, sessionExpiresAt);
    pinModalState.close();
  });

  return (
    <>
      <AsyncSelect<Option<CompanyIdentifiersFragment>>
        onChange={handleChange}
        placeholder={t('common:SELECT_COMPANY')}
        defaultOptions
        loadOptions={loadOptions}
        isClearable
        isLoading={pending}
        value={
          selectedCompany
            ? {
                label: selectedCompany.name,
                value: selectedCompany,
              }
            : null
        }
      />
      {pinModalState.isMounted && (
        <VerifyPinModal
          state={pinModalState}
          companyId={pinModalState.payload.id}
          companyName={pinModalState.payload.name}
          onConfirmed={handlePinConfirmed}
        />
      )}
    </>
  );
};

export default CompaniesSwitch;
